import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n";
import { api } from "../services/api";
import { PLAN_LIMITS, PLAN_FEATURES } from "../services/plans";
import ChannelPicker from "../components/ChannelPicker";
import UpgradeNudge from "../components/UpgradeNudge";
import ConfirmDialog from "../components/ConfirmDialog";
import "./Dashboard.css";
import { QRCodeSVG } from "qrcode.react";

const INTEGRATION_CATALOG = [
  { service: "notion", icon: "📝", comingSoon: false },
  { service: "obsidian", icon: "💎", comingSoon: true },
  { service: "todoist", icon: "✅", comingSoon: true },
];

const PLATFORM_CATALOG = [
  { platform: "telegram", icon: "✈", color: "#0088cc", comingSoon: false },
  { platform: "whatsapp", icon: "💬", color: "#25D366", comingSoon: false },
];

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("channels");
  const pickerRef = useRef();
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ timezone: "", delivery_time: "", password: "" });
  const [profileMsg, setProfileMsg] = useState("");

  // Integrations state
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [connectingNotion, setConnectingNotion] = useState(false);
  const [integrationToast, setIntegrationToast] = useState(null);

  // Database selection state
  const [notionDatabases, setNotionDatabases] = useState(null);
  const [databasesLoading, setDatabasesLoading] = useState(false);
  const [showDbSelector, setShowDbSelector] = useState(false);

  // Platform state
  const [platformStatuses, setPlatformStatuses] = useState({});
  const [platformLinkUrl, setPlatformLinkUrl] = useState(null);
  const [platformConnecting, setPlatformConnecting] = useState(null);
  const [platformPolling, setPlatformPolling] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [platformLinkExpired, setPlatformLinkExpired] = useState(false);

  // Preset subscriptions
  const [presetSubscriptions, setPresetSubscriptions] = useState([]);

  // Subscription upgrade state
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showUpgradeNudge, setShowUpgradeNudge] = useState(false);

  // Subscription cancel state
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [pendingCancellationDate, setPendingCancellationDate] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // ConfirmDialog state for existing confirms
  const [confirmDialog, setConfirmDialog] = useState(null);

  const navigate = useNavigate();

  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const handleUpgrade = async (plan) => {
    setUpgradeLoading(true);
    try {
      const { checkout_url } = await api.createCheckout(plan);
      if (checkout_url.startsWith("http") && !checkout_url.includes(window.location.host)) {
        window.location.href = checkout_url;
      } else {
        const path = checkout_url.startsWith("http")
          ? new URL(checkout_url).pathname + new URL(checkout_url).search
          : checkout_url;
        navigate(path);
      }
    } catch (err) {
      setUpgradeLoading(false);
      if (err.status === 400) {
        setIntegrationToast({ type: "error", message: err.message });
      } else {
        setIntegrationToast({ type: "error", message: t("subscription.errorGeneric") });
      }
    }
  };

  const fetchSubs = useCallback(async () => {
    try { setSubscriptions(await api.listSubscriptions()); } catch {} finally { setLoading(false); }
  }, []);

  const fetchPresetSubs = useCallback(async () => {
    try { setPresetSubscriptions(await api.listPresetSubscriptions()); } catch {}
  }, []);

  const fetchIntegrations = useCallback(async () => {
    setIntegrationsLoading(true);
    try { setIntegrations(await api.listIntegrations()); } catch {} finally { setIntegrationsLoading(false); }
  }, []);

  const fetchPlatformStatuses = useCallback(async () => {
    const statuses = {};
    for (const p of PLATFORM_CATALOG.filter((c) => !c.comingSoon)) {
      try {
        const res = await api.getPlatformStatus(p.platform);
        statuses[p.platform] = res.linked;
      } catch {
        statuses[p.platform] = false;
      }
    }
    setPlatformStatuses(statuses);
  }, []);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const sub = await api.getSubscription();
      setHasActiveSubscription(sub.has_active_subscription);
      if (sub.access_valid_until && !sub.has_active_subscription) {
        const formattedDate = new Intl.DateTimeFormat(
          document.documentElement.lang || "en",
          { year: "numeric", month: "long", day: "numeric" }
        ).format(new Date(sub.access_valid_until));
        setPendingCancellationDate(formattedDate);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchSubs(); fetchPresetSubs(); fetchIntegrations(); fetchPlatformStatuses(); fetchSubscriptionStatus(); }, [fetchSubs, fetchPresetSubs, fetchIntegrations, fetchPlatformStatuses, fetchSubscriptionStatus]);

  useEffect(() => {
    if (user) setProfileForm({ timezone: user.timezone, delivery_time: user.delivery_time?.slice(0, 5) || "07:00", password: "" });
  }, [user]);

  // Handle OAuth redirect query params
  useEffect(() => {
    const integration = searchParams.get("integration");
    const status = searchParams.get("status");
    if (integration && status) {
      setActiveTab("integrations");
      if (status === "success") {
        setIntegrationToast({ type: "success", message: t("dashboard.notionSuccess") });
        fetchIntegrations();
      } else {
        setIntegrationToast({ type: "error", message: t("dashboard.notionError") });
      }
      // Clean URL
      searchParams.delete("integration");
      searchParams.delete("status");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss toast
  useEffect(() => {
    if (integrationToast) {
      const timer = setTimeout(() => setIntegrationToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [integrationToast]);

  const handleConnectNotion = async () => {
    setConnectingNotion(true);
    try {
      const { authorize_url } = await api.authorizeNotion();
      window.location.href = authorize_url;
    } catch (err) {
      if (err.status === 503) {
        setIntegrationToast({ type: "error", message: t("dashboard.notionUnavailable") });
      } else {
        setIntegrationToast({ type: "error", message: err.message });
      }
      setConnectingNotion(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const result = await api.cancelSubscription();
      setShowCancelDialog(false);
      setCancelLoading(false);
      setHasActiveSubscription(false);

      if (result.immediate) {
        setIntegrationToast({ type: "success", message: t("subscription.cancelledRefunded") });
      } else {
        const formattedDate = new Intl.DateTimeFormat(
          document.documentElement.lang || "en",
          { year: "numeric", month: "long", day: "numeric" }
        ).format(new Date(result.access_valid_until));
        setPendingCancellationDate(formattedDate);
        setIntegrationToast({
          type: "success",
          message: t("subscription.cancelledKeepAccess", {
            plan: capitalize(user?.plan),
            date: formattedDate,
          }),
        });
      }
      refreshUser().catch(() => {});
      fetchSubscriptionStatus();
    } catch (err) {
      setCancelLoading(false);
      setShowCancelDialog(false);
      if (err.status === 400) {
        setIntegrationToast({ type: "error", message: t("subscription.noActiveSubscription") });
        setHasActiveSubscription(false);
      } else {
        setIntegrationToast({ type: "error", message: t("subscription.errorGeneric") });
      }
    }
  };

  const handleDisconnect = (service) => {
    setConfirmDialog({
      title: t("dashboard.disconnect"),
      body: t("dashboard.disconnectConfirm"),
      confirmLabel: t("dashboard.disconnect"),
      dismissLabel: t("dashboard.disconnectDismiss"),
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.disconnectIntegration(service);
          setIntegrationToast({ type: "success", message: t("dashboard.notionDisconnected") });
          fetchIntegrations();
        } catch (err) {
          setIntegrationToast({ type: "error", message: err.message });
        }
      },
    });
  };

  const handleConnectPlatform = async (platform) => {
    setPlatformConnecting(platform);
    setPlatformLinkUrl(null);
    setPlatformLinkExpired(false);
    try {
      const { authorize_url } = await api.authorizePlatform(platform);
      setPlatformLinkUrl(authorize_url);
      // Start polling for link confirmation
      setPlatformPolling(platform);
      if (platform === "whatsapp") {
        setShowQrModal(true);
      }
    } catch (err) {
      if (err.status === 503) {
        setIntegrationToast({ type: "error", message: t(`dashboard.${platform}Unavailable`) });
      } else {
        setIntegrationToast({ type: "error", message: err.message });
      }
      setPlatformConnecting(null);
    }
  };

  const handleDisconnectPlatform = (platform) => {
    const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
    setConfirmDialog({
      title: t("dashboard.disconnect"),
      body: t(`dashboard.disconnect${platformName}Confirm`),
      confirmLabel: t("dashboard.disconnect"),
      dismissLabel: t("dashboard.disconnectDismiss"),
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.disconnectPlatform(platform);
          setPlatformStatuses((prev) => ({ ...prev, [platform]: false }));
          setIntegrationToast({ type: "success", message: t(`dashboard.${platform}Disconnected`) });
        } catch (err) {
          if (err.status === 404) {
            setPlatformStatuses((prev) => ({ ...prev, [platform]: false }));
          } else {
            setIntegrationToast({ type: "error", message: err.message });
          }
        }
      },
    });
  };

  // Poll for platform link status
  useEffect(() => {
    if (!platformPolling) return;
    const interval = setInterval(async () => {
      try {
        const { linked } = await api.getPlatformStatus(platformPolling);
        if (linked) {
          setPlatformStatuses((prev) => ({ ...prev, [platformPolling]: true }));
          setShowQrModal(false);
          setPlatformPolling(null);
          setPlatformConnecting(null);
          setPlatformLinkUrl(null);
          setPlatformLinkExpired(false);
          setIntegrationToast({ type: "success", message: t(`dashboard.${platformPolling}Connected`) });
        }
      } catch {}
    }, 3000);
    // Stop polling after 2 minutes
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setPlatformPolling(null);
      setPlatformLinkExpired(true);
    }, 120000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [platformPolling, t]);

  const fetchNotionDatabases = useCallback(async () => {
    setDatabasesLoading(true);
    try {
      const dbs = await api.listNotionDatabases();
      setNotionDatabases(dbs);
    } catch {
      setNotionDatabases([]);
    } finally {
      setDatabasesLoading(false);
    }
  }, []);

  const handleSelectDatabase = async (databaseId, databaseName) => {
    try {
      await api.selectNotionDatabase(databaseId, databaseName);
      setIntegrationToast({ type: "success", message: t("dashboard.databaseSelected") });
      setShowDbSelector(false);
      setNotionDatabases(null);
      fetchIntegrations();
    } catch (err) {
      setIntegrationToast({ type: "error", message: err.message });
    }
  };

  // Auto-fetch databases when Notion is connected but no database selected
  useEffect(() => {
    const notion = integrations.find((i) => i.service === "notion");
    if (notion && !notion.database_name && activeTab === "integrations") {
      fetchNotionDatabases();
    }
  }, [integrations, activeTab, fetchNotionDatabases]);

  const handleUnsubscribe = async (channelId) => {
    try { await api.unsubscribe(channelId); setSubscriptions((prev) => prev.filter((s) => s.channel.id !== channelId)); } catch {}
  };

  const handleUnsubscribePreset = async (presetId) => {
    try {
      await api.unsubscribePreset(presetId);
      setPresetSubscriptions((prev) => prev.filter((p) => p.preset.id !== presetId));
      setIntegrationToast({ type: "success", message: t("dashboard.presetRemoved") });
    } catch {
      setIntegrationToast({ type: "error", message: t("subscription.errorGeneric") });
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileMsg("");
    const data = {};
    if (profileForm.timezone !== user.timezone) data.timezone = profileForm.timezone;
    if (profileForm.delivery_time + ":00" !== user.delivery_time) data.delivery_time = profileForm.delivery_time + ":00";
    if (profileForm.password) data.password = profileForm.password;
    if (!Object.keys(data).length) { setProfileMsg(t("dashboard.noChanges")); return; }
    try {
      await api.updateMe(data);
      await refreshUser();
      setProfileMsg(t("dashboard.profileUpdated"));
      setEditingProfile(false);
      setProfileForm((f) => ({ ...f, password: "" }));
    } catch (err) { setProfileMsg(err.message); }
  };

  const getConnectedIntegration = (service) => integrations.find((i) => i.service === service);

  const limit = PLAN_LIMITS[user?.plan] ?? 0;
  const features = PLAN_FEATURES[user?.plan] || PLAN_FEATURES.free;
  const used = subscriptions.length;
  const presetLimit = PLAN_FEATURES[user?.plan]?.presets ?? 0;
  const pct = limit === 0 ? 100 : limit === Infinity ? 0 : Math.round((used / limit) * 100);
  const hasPlatform = Object.values(platformStatuses).some(Boolean);
  const needsSetup = !loading && used === 0 && !hasPlatform;

  return (
    <div className="dashboard">
      <div className="dash-sidebar">
        <div className="dash-profile">
          <div className="dash-avatar">{user?.email?.[0]?.toUpperCase()}</div>
          <div className="dash-user-info">
            <div className="dash-user-email">{user?.email}</div>
            <div className="dash-user-plan">{user?.plan} {t("dashboard.plan")}</div>
          </div>
        </div>

        <nav className="dash-nav">
          <button className={`dash-nav-item ${activeTab === "channels" ? "active" : ""}`} onClick={() => setActiveTab("channels")}>
            <span className="dash-nav-icon">📡</span>{t("dashboard.channels")}<span className="dash-nav-count">{used}</span>
          </button>
          <button className={`dash-nav-item ${activeTab === "integrations" ? "active" : ""}`} onClick={() => setActiveTab("integrations")}>
            <span className="dash-nav-icon">🔗</span>{t("dashboard.integrations")}
            {integrations.length > 0 && <span className="dash-nav-count">{integrations.length}</span>}
          </button>
          <button className={`dash-nav-item ${activeTab === "profile" ? "active" : ""}`} onClick={() => setActiveTab("profile")}>
            <span className="dash-nav-icon">⚙</span>{t("dashboard.settings")}
          </button>
        </nav>

        <div className="dash-bot-hint">
          <span className="bot-hint-icon">💬</span>
          <span className="bot-hint-text" dangerouslySetInnerHTML={{ __html: t("dashboard.botHint") }} />
        </div>

        <div className="dash-usage">
          <div className="usage-header">
            <span>{t("dashboard.channelUsage")}</span>
            <span className="usage-count">{used}/{limit}</span>
          </div>
          <div className="usage-bar">
            <div className="usage-fill" style={{ width: limit === 0 ? "100%" : `${Math.min(pct, 100)}%` }} />
          </div>
          {pct >= 80 && <a href="/pricing" className="usage-upgrade">{t("dashboard.upgradePlan")}</a>}
        </div>
      </div>

      <div className="dash-main">
        {/* Integration toast */}
        {integrationToast && (
          <div className={`integration-toast toast-${integrationToast.type}`}>
            <span>{integrationToast.message}</span>
            <button className="toast-close" onClick={() => setIntegrationToast(null)}>×</button>
          </div>
        )}

        {needsSetup && (
          <div className="setup-banner">
            <div className="setup-banner-text">
              <h3>{t("onboarding.finishSetupBanner")}</h3>
              <p>{t("onboarding.finishSetupDesc")}</p>
            </div>
            <Link to="/onboarding" className="btn-primary btn-sm">{t("onboarding.finishSetupBtn")}</Link>
          </div>
        )}

        {activeTab === "channels" && (
          <div className="dash-panel">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">{t("dashboard.yourChannels")}</h2>
                <p className="panel-desc">{t("dashboard.yourChannelsDesc")}</p>
              </div>
              <button className="btn-primary btn-sm" onClick={() => setActiveTab("add")}>{t("dashboard.addChannel")}</button>
            </div>

            {loading ? (
              <div className="panel-loading">{[1, 2, 3].map((i) => <div key={i} className="skeleton-row" />)}</div>
            ) : subscriptions.length === 0 && presetSubscriptions.length === 0 ? (
              <div className="panel-empty">
                <div className="empty-icon">📡</div>
                <h3>{t("dashboard.noChannels")}</h3>
                <p>{t("dashboard.noChannelsDesc")}</p>
                <button className="btn-primary btn-sm" onClick={() => setActiveTab("add")}>{t("dashboard.addFirstChannel")}</button>
              </div>
            ) : (
              <>
                {presetSubscriptions.length > 0 && (
                  <div className="channels-list" style={{ marginBottom: 24 }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "var(--accent)", textTransform: "uppercase", marginBottom: 10 }}>
                      {t("dashboard.presetsSection")}
                    </div>
                    {presetSubscriptions.map((sub) => (
                      <div key={sub.id} className="channel-row">
                        <div className="channel-info">
                          <div className="channel-avatar" style={{ background: "var(--accent-glow)", border: "1px solid var(--border-accent)", color: "var(--accent)" }}>
                            {sub.preset.name[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="channel-name">{sub.preset.name}</div>
                            {sub.preset.description && <div className="channel-id">{sub.preset.description}</div>}
                          </div>
                        </div>
                        <div className="channel-meta">
                          <span className="channel-date">{t("dashboard.since")} {new Date(sub.created_at).toLocaleDateString()}</span>
                          <button className="btn-danger-ghost btn-sm" onClick={() => handleUnsubscribePreset(sub.preset.id)}>{t("dashboard.unsubscribe")}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {subscriptions.length > 0 && (
                  <div className="channels-list">
                    {presetSubscriptions.length > 0 && (
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 10 }}>
                        {t("dashboard.channels")}
                      </div>
                    )}
                    {subscriptions.map((sub) => (
                      <div key={sub.id} className="channel-row">
                        <div className="channel-info">
                          <div className="channel-avatar">{sub.channel.name[0].toUpperCase()}</div>
                          <div>
                            <div className="channel-name">{sub.channel.name}</div>
                            <div className="channel-id">{sub.channel.youtube_channel_id}</div>
                          </div>
                        </div>
                        <div className="channel-meta">
                          <span className="channel-date">{t("dashboard.since")} {new Date(sub.created_at).toLocaleDateString()}</span>
                          <button className="btn-danger-ghost btn-sm" onClick={() => handleUnsubscribe(sub.channel.id)}>{t("dashboard.unsubscribe")}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "add" && (
          <div className="dash-panel">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">{t("dashboard.addChannelsTitle")}</h2>
                <p className="panel-desc">{t("dashboard.addChannelsDesc")}</p>
              </div>
            </div>

            <ChannelPicker
              ref={pickerRef}
              planPresetLimit={presetLimit}
              planChannelLimit={limit}
              currentPresetCount={presetSubscriptions.length}
              currentChannelCount={used}
              subscribedChannelIds={subscriptions.map(s => s.channel.youtube_channel_id)}
              subscribedPresetIds={presetSubscriptions.map(s => s.preset.id)}
              showUrlInput
              onUpgrade={() => {
                const nextPlan = user?.plan === "free" ? "starter" : user?.plan === "starter" ? "pro" : null;
                if (nextPlan) handleUpgrade(nextPlan);
              }}
            />

            <div className="subscribe-actions" style={{ marginTop: 20 }}>
              <span className="subscribe-hint">
                {limit > 0
                  ? t("dashboard.channelsRemaining", { count: Math.max(0, limit - used), plan: user?.plan })
                  : t("dashboard.noCustomChannels")}
              </span>
              <button
                type="button"
                className="btn-primary btn-md"
                disabled={subscribing}
                onClick={async () => {
                  const { selectedPresetIds, selectedChannelIds, manualUrls, categories } = pickerRef.current.getSelection();
                  const allUrls = [...selectedChannelIds, ...manualUrls];
                  if (!allUrls.length && !selectedPresetIds.length) return;
                  setSubscribing(true);
                  setResult(null);
                  try {
                    const subscribedPresets = [];
                    for (const presetId of selectedPresetIds) {
                      const res = await api.subscribePreset(presetId);
                      if (res) subscribedPresets.push(res);
                    }
                    let channelResult = null;
                    if (allUrls.length) {
                      channelResult = await api.subscribe(allUrls, categories);
                    }
                    setResult({
                      subscribed: [...subscribedPresets.map(p => ({ id: p.preset?.id || p.id, name: p.preset?.name || "Preset" })), ...(channelResult?.subscribed || [])],
                      already_subscribed: channelResult?.already_subscribed || [],
                      failed: channelResult?.failed || [],
                    });
                    fetchSubs(); fetchPresetSubs();
                  }
                  catch (err) {
                    if (err.message && err.message.toLowerCase().includes("plan limit")) {
                      setShowUpgradeNudge(true);
                    } else {
                      setResult({ error: err.message });
                    }
                  }
                  finally { setSubscribing(false); }
                }}
              >
                {subscribing ? <span className="btn-spinner" /> : t("dashboard.subscribe")}
              </button>
            </div>

            {showUpgradeNudge && (
              <UpgradeNudge
                currentPlan={user?.plan || "free"}
                onUpgrade={() => {
                  const nextPlan = user?.plan === "starter" ? "pro" : "starter";
                  handleUpgrade(nextPlan);
                }}
                onDismiss={() => setShowUpgradeNudge(false)}
                loading={upgradeLoading}
              />
            )}

            {result && !result.error && (
              <div className="subscribe-result">
                {result.subscribed?.length > 0 && (
                  <div className="result-group result-success">
                    <h4>✓ {t("dashboard.subscribed")} ({result.subscribed.length})</h4>
                    {result.subscribed.map((c) => <div key={c.id} className="result-item">{c.name}</div>)}
                  </div>
                )}
                {result.already_subscribed?.length > 0 && (
                  <div className="result-group result-info">
                    <h4>{t("dashboard.alreadySubscribed")} ({result.already_subscribed.length})</h4>
                    {result.already_subscribed.map((c) => <div key={c.id} className="result-item">{c.name}</div>)}
                  </div>
                )}
                {result.failed?.length > 0 && (
                  <div className="result-group result-error">
                    <h4>{t("dashboard.failed")} ({result.failed.length})</h4>
                    {result.failed.map((f, i) => <div key={i} className="result-item">{f.url}: {f.error}</div>)}
                  </div>
                )}
              </div>
            )}
            {result?.error && (
              <div className="subscribe-result">
                <div className="result-group result-error">
                  <h4>{t("dashboard.error")}</h4>
                  <div className="result-item">{result.error}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "integrations" && (
          <div className="dash-panel">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">{t("dashboard.integrationsTitle")}</h2>
                <p className="panel-desc">{t("dashboard.integrationsDesc")}</p>
              </div>
            </div>

            {/* Platforms section */}
            <h3 className="integrations-section-title">{t("dashboard.platformsSection")}</h3>
            <div className="integrations-grid" style={{ marginBottom: 32 }}>
              {PLATFORM_CATALOG.map((cat) => {
                const isLinked = platformStatuses[cat.platform];
                const isConnecting = platformConnecting === cat.platform;
                const planGated = cat.platform === "whatsapp" && !features.whatsapp;

                return (
                  <div key={cat.platform} className={`integration-card ${isLinked ? "integration-connected" : ""} ${cat.comingSoon ? "integration-disabled" : ""} ${planGated ? "integration-gated" : ""}`}
                    style={{ "--platform-color": cat.color }}
                  >
                    <div className="integration-header">
                      <div className="platform-icon" style={{ background: cat.color }}>{cat.icon}</div>
                      <div className="integration-name">
                        {cat.platform.charAt(0).toUpperCase() + cat.platform.slice(1)}
                      </div>
                      {isLinked && (
                        <span className="integration-status">{t("dashboard.connected")}</span>
                      )}
                      {cat.comingSoon && (
                        <span className="integration-coming-soon">{t("dashboard.comingSoon")}</span>
                      )}
                      {planGated && (
                        <span className="integration-coming-soon">{t("dashboard.requiresStarter")}</span>
                      )}
                    </div>

                    <p className="integration-desc">
                      {t(`dashboard.${cat.platform}Desc`)}
                    </p>

                    {planGated ? (
                      <Link to="/pricing" className="btn-primary btn-sm">{t("dashboard.upgradePlan")}</Link>
                    ) : isLinked ? (
                      <div className="integration-details">
                        <button
                          className="btn-danger-ghost btn-sm"
                          onClick={() => handleDisconnectPlatform(cat.platform)}
                        >
                          {t("dashboard.disconnect")}
                        </button>
                      </div>
                    ) : !cat.comingSoon ? (
                      <div className="platform-connect-flow">
                        {!isConnecting ? (
                          <button
                            className="btn-primary btn-sm"
                            onClick={() => handleConnectPlatform(cat.platform)}
                          >
                            {t(`dashboard.connect${cat.platform.charAt(0).toUpperCase() + cat.platform.slice(1)}`)}
                          </button>
                        ) : cat.platform === "whatsapp" ? (
                          showQrModal && platformLinkUrl ? (
                            <div className="qr-modal-overlay" onClick={() => { setShowQrModal(false); setPlatformPolling(null); setPlatformConnecting(null); setPlatformLinkUrl(null); setPlatformLinkExpired(false); }}>
                              <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="qr-modal-header">
                                  <h3>{t("dashboard.whatsappQrTitle")}</h3>
                                  <button className="toast-close" onClick={() => { setShowQrModal(false); setPlatformPolling(null); setPlatformConnecting(null); setPlatformLinkUrl(null); setPlatformLinkExpired(false); }}>×</button>
                                </div>
                                {!platformLinkExpired ? (
                                  <>
                                    <div className="qr-code-container">
                                      {/* fgColor matches --text-primary for dark theme */}
                                      <QRCodeSVG value={platformLinkUrl} size={200} bgColor="transparent" fgColor="#e8eaf0" level="M" />
                                    </div>
                                    <p className="qr-instructions">{t("dashboard.whatsappLinkReady")}</p>
                                    <a href={platformLinkUrl} target="_blank" rel="noopener noreferrer" className="qr-fallback-link">
                                      {t("dashboard.openWhatsapp")} ↗
                                    </a>
                                    {platformPolling && (
                                      <div className="platform-polling">
                                        <div className="polling-dot" style={{ background: cat.color }} />
                                        <span>{t("dashboard.whatsappWaiting")}</span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="qr-expired">
                                    <p className="qr-expired-text">{t("dashboard.whatsappLinkExpired")}</p>
                                    <button className="btn-primary btn-sm" onClick={() => handleConnectPlatform("whatsapp")}>
                                      {t("dashboard.whatsappGenerateNew")}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null
                        ) : (
                          <div className="platform-link-section">
                            <p className="platform-link-instructions">
                              {t("dashboard.telegramLinkReady")}
                            </p>
                            {platformLinkUrl && (
                              <a
                                href={platformLinkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-platform"
                                style={{ background: cat.color }}
                              >
                                {t("dashboard.openTelegram")} ↗
                              </a>
                            )}
                            {platformPolling && (
                              <div className="platform-polling">
                                <div className="polling-dot" style={{ background: cat.color }} />
                                <span>{t("dashboard.telegramWaiting")}</span>
                              </div>
                            )}
                            {platformLinkExpired && !platformPolling && (
                              <div className="platform-expired">
                                <p className="platform-expired-text">{t(`dashboard.${cat.platform}LinkExpired`)}</p>
                                <button className="btn-primary btn-sm" onClick={() => handleConnectPlatform(cat.platform)}>
                                  {t(`dashboard.${cat.platform}GenerateNew`)}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {/* Services section */}
            <h3 className="integrations-section-title">{t("dashboard.servicesSection")}</h3>
            {integrationsLoading ? (
              <div className="panel-loading">{[1, 2].map((i) => <div key={i} className="skeleton-row" />)}</div>
            ) : (
              <div className="integrations-grid">
                {INTEGRATION_CATALOG.map((cat) => {
                  const connected = getConnectedIntegration(cat.service);
                  const needsDatabase = connected && !connected.database_name;
                  const isFullyConfigured = connected && connected.database_name;
                  const servicePlanGated = cat.service === "notion" && !features.notion;

                  return (
                    <div key={cat.service} className={`integration-card ${isFullyConfigured ? "integration-connected" : ""} ${needsDatabase ? "integration-needs-setup" : ""} ${cat.comingSoon ? "integration-disabled" : ""} ${servicePlanGated ? "integration-gated" : ""}`}>
                      <div className="integration-header">
                        <div className="integration-icon">{cat.icon}</div>
                        <div className="integration-name">
                          {cat.service.charAt(0).toUpperCase() + cat.service.slice(1)}
                        </div>
                        {isFullyConfigured && (
                          <span className="integration-status">{t("dashboard.connected")}</span>
                        )}
                        {needsDatabase && (
                          <span className="integration-status integration-status-warning">Setup</span>
                        )}
                        {cat.comingSoon && (
                          <span className="integration-coming-soon">{t("dashboard.comingSoon")}</span>
                        )}
                        {servicePlanGated && (
                          <span className="integration-coming-soon">{t("dashboard.requiresPro")}</span>
                        )}
                      </div>

                      <p className="integration-desc">
                        {t(`dashboard.${cat.service}Desc`)}
                      </p>

                      {servicePlanGated ? (
                        <Link to="/pricing" className="btn-primary btn-sm">{t("dashboard.upgradePlan")}</Link>
                      ) : connected ? (
                        <div className="integration-details">
                          {connected.workspace_name && (
                            <div className="integration-detail">
                              <span className="detail-label">{t("dashboard.workspace")}</span>
                              <span className="detail-value">{connected.workspace_name}</span>
                            </div>
                          )}
                          {isFullyConfigured && (
                            <div className="integration-detail">
                              <span className="detail-label">{t("dashboard.database")}</span>
                              <span className="detail-value">{connected.database_name}</span>
                            </div>
                          )}
                          <div className="integration-detail">
                            <span className="detail-label">{t("dashboard.connectedOn")}</span>
                            <span className="detail-value">{new Date(connected.created_at).toLocaleDateString()}</span>
                          </div>

                          {/* Database selector: shown when no DB selected or user clicks "Change" */}
                          {(needsDatabase || showDbSelector) && (
                            <div className="db-selector">
                              {needsDatabase && !showDbSelector && (
                                <div className="db-warning">{t("dashboard.selectDatabase")}</div>
                              )}
                              {databasesLoading ? (
                                <div className="db-loading">{t("dashboard.loadingDatabases")}</div>
                              ) : notionDatabases && notionDatabases.length === 0 ? (
                                <div className="db-empty">{t("dashboard.noDatabases")}</div>
                              ) : notionDatabases ? (
                                <div className="db-list">
                                  {notionDatabases.map((db) => (
                                    <button
                                      key={db.id}
                                      className="db-option"
                                      onClick={() => handleSelectDatabase(db.id, db.title)}
                                    >
                                      <span className="db-option-title">{db.title}</span>
                                      <span className="db-option-select">Select</span>
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                              {showDbSelector && (
                                <button className="btn-ghost btn-xs" style={{ marginTop: 8 }} onClick={() => { setShowDbSelector(false); setNotionDatabases(null); }}>
                                  {t("dashboard.cancel")}
                                </button>
                              )}
                            </div>
                          )}

                          {/* Change database button (only when fully configured and not already showing selector) */}
                          {isFullyConfigured && !showDbSelector && (
                            <button
                              className="btn-ghost btn-xs"
                              onClick={() => { setShowDbSelector(true); fetchNotionDatabases(); }}
                              style={{ marginTop: 8 }}
                            >
                              {t("dashboard.changeDatabase")}
                            </button>
                          )}

                          <button
                            className="btn-danger-ghost btn-sm"
                            onClick={() => handleDisconnect(cat.service)}
                            style={{ marginTop: needsDatabase || showDbSelector ? 4 : 8 }}
                          >
                            {t("dashboard.disconnect")}
                          </button>
                        </div>
                      ) : !cat.comingSoon ? (
                        <button
                          className="btn-primary btn-sm"
                          onClick={handleConnectNotion}
                          disabled={connectingNotion}
                        >
                          {connectingNotion ? t("dashboard.connecting") : t("dashboard.connectNotion")}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "profile" && (
          <div className="dash-panel">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">{t("dashboard.settingsTitle")}</h2>
                <p className="panel-desc">{t("dashboard.settingsDesc")}</p>
              </div>
              {!editingProfile && (
                <button className="btn-ghost btn-sm" onClick={() => { setEditingProfile(true); setProfileMsg(""); }}>{t("dashboard.edit")}</button>
              )}
            </div>

            {profileMsg && (
              <div className={`profile-msg ${profileMsg === t("dashboard.profileUpdated") ? "msg-success" : "msg-info"}`}>{profileMsg}</div>
            )}

            <form onSubmit={handleProfileSave} className="profile-form">
              <div className="profile-grid">
                <div className="profile-field">
                  <label className="form-label">{t("login.email")}</label>
                  <div className="profile-value">{user?.email}</div>
                </div>
                <div className="profile-field">
                  <label className="form-label">{t("dashboard.planLabel")}</label>
                  <div className="profile-value">
                    <span className="plan-tag">{user?.plan}</span>
                    {user?.plan !== "pro" && (
                      <button type="button" className="upgrade-link" onClick={() => navigate("/pricing")}>
                        {t("dashboard.upgrade")}
                      </button>
                    )}
                    {user?.plan !== "free" && hasActiveSubscription && !pendingCancellationDate && (
                      <button type="button" className="cancel-sub-btn" onClick={() => setShowCancelDialog(true)}>
                        <span className="cancel-x">&times;</span> {t("subscription.cancelButton")}
                      </button>
                    )}
                  </div>
                  {pendingCancellationDate && (
                    <p className="billing-pending-inline">
                      {t("subscription.pendingCancellation", { date: pendingCancellationDate })}
                    </p>
                  )}
                </div>
                <div className="profile-field">
                  <label className="form-label">{t("login.timezone")}</label>
                  {editingProfile ? (
                    <select value={profileForm.timezone} onChange={(e) => setProfileForm({ ...profileForm, timezone: e.target.value })} className="form-input">
                      {["America/Sao_Paulo","America/New_York","America/Chicago","America/Denver","America/Los_Angeles","Europe/London","Europe/Berlin","Asia/Tokyo","Australia/Sydney"].map((tz) => (
                        <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  ) : <div className="profile-value">{user?.timezone?.replace(/_/g, " ")}</div>}
                </div>
                <div className="profile-field">
                  <label className="form-label">{t("login.deliveryTime")}</label>
                  {editingProfile ? (
                    <input type="time" value={profileForm.delivery_time} onChange={(e) => setProfileForm({ ...profileForm, delivery_time: e.target.value })} className="form-input" />
                  ) : <div className="profile-value">{user?.delivery_time?.slice(0, 5)}</div>}
                </div>
                {editingProfile && (
                  <div className="profile-field">
                    <label className="form-label">{t("dashboard.newPassword")}</label>
                    <input type="password" value={profileForm.password} onChange={(e) => setProfileForm({ ...profileForm, password: e.target.value })}
                      placeholder={t("dashboard.newPasswordPlaceholder")} className="form-input" />
                  </div>
                )}
              </div>
              {editingProfile && (
                <div className="profile-actions">
                  <button type="button" className="btn-ghost btn-sm" onClick={() => { setEditingProfile(false); setProfileMsg(""); }}>{t("dashboard.cancel")}</button>
                  <button type="submit" className="btn-primary btn-sm">{t("dashboard.saveChanges")}</button>
                </div>
              )}
            </form>

            <div className="danger-zone">
              <h3 className="danger-title">{t("dashboard.dangerZone")}</h3>
              <p className="danger-desc">{t("dashboard.dangerDesc")}</p>
              <button className="btn-danger btn-sm" onClick={() => {
                setConfirmDialog({
                  title: t("dashboard.dangerZone"),
                  body: t("dashboard.deleteConfirm"),
                  confirmLabel: t("dashboard.deleteAccount"),
                  dismissLabel: t("dashboard.deleteDismiss"),
                  variant: "danger",
                  onConfirm: async () => {
                    await api.deleteMe();
                    window.location.href = "/";
                  },
                });
              }}>{t("dashboard.deleteAccount")}</button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel subscription dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        title={t("subscription.cancelDialogTitle")}
        body={t("subscription.cancelDialogBody", { plan: capitalize(user?.plan) })}
        confirmLabel={t("subscription.cancelDialogConfirm")}
        dismissLabel={t("subscription.cancelDialogDismiss")}
        variant="danger"
        loading={cancelLoading}
        onConfirm={handleCancelSubscription}
        onDismiss={() => setShowCancelDialog(false)}
      />

      {/* Generic confirm dialog (for disconnect/delete) */}
      <ConfirmDialog
        open={!!confirmDialog}
        title={confirmDialog?.title}
        body={confirmDialog?.body}
        confirmLabel={confirmDialog?.confirmLabel}
        dismissLabel={confirmDialog?.dismissLabel}
        variant={confirmDialog?.variant || "danger"}
        loading={confirmDialog?.loading}
        onConfirm={async () => {
          if (confirmDialog?.onConfirm) {
            setConfirmDialog((prev) => ({ ...prev, loading: true }));
            try {
              await confirmDialog.onConfirm();
            } finally {
              setConfirmDialog(null);
            }
          } else {
            setConfirmDialog(null);
          }
        }}
        onDismiss={() => setConfirmDialog(null)}
      />
    </div>
  );
}
